import { useEffect, useRef } from "react";

import { useGoogleAdsOAuth } from "app/pages/digital-marketing/shared/google-ads/hooks/use-google-ads-oauth";

import type { GoogleAdsActionType } from "./google-ads-actions.types";

interface IUseGoogleAdsSelectionActionParams {
    // Called once an action is authorized - either the user was already connected, or a
    // silent reconnect just succeeded.
    onAuthorized: (actionType: GoogleAdsActionType) => void;
}

interface IUseGoogleAdsSelectionActionReturn {
    // Requests the given action: hands off immediately when already connected,
    // otherwise silently re-runs the GIS OAuth popup (SIM-111/`useGoogleAdsOAuth`) and hands off
    // only if it succeeds.
    dispatchAction: (actionType: GoogleAdsActionType) => void;
    isConnecting: boolean;
}

/**
 * Encapsulates the auth-gated dispatch decision behind the "Google Ads Actions" selection-bar
 * control (SIM-112): reuse an existing connection, or silently reconnect first.
 *
 * The reconnect never re-shows the account/campaign/ad-group picker - the stored linkage already
 * resolves the target ad group, so `useGoogleAdsOAuth().connect()` (connection only) is all that's
 * needed. Declining/closing the OAuth popup resolves `isConnecting` back to `false` without ever
 * connecting, which this hook treats as a clean cancellation - no handoff, no error
 * surfaced.
 */
export const useGoogleAdsSelectionAction = ({
    onAuthorized,
}: IUseGoogleAdsSelectionActionParams): IUseGoogleAdsSelectionActionReturn => {
    const { isConnected, isConnecting, connect, checkIsConnected } = useGoogleAdsOAuth();
    const pendingActionRef = useRef<GoogleAdsActionType | null>(null);
    const wasConnectingRef = useRef(false);

    useEffect(() => {
        const reconnectJustFinished = wasConnectingRef.current && !isConnecting;
        wasConnectingRef.current = isConnecting;

        if (!reconnectJustFinished || !pendingActionRef.current) {
            return;
        }

        const actionType = pendingActionRef.current;
        pendingActionRef.current = null;

        // Connected now means the popup succeeded; still not connected means the user declined
        // or closed it (or the flow errored) - either way, cancel silently per SIM-112 scope.
        if (isConnected) {
            onAuthorized(actionType);
        }
    }, [isConnecting, isConnected, onAuthorized]);

    const dispatchAction = (actionType: GoogleAdsActionType): void => {
        if (checkIsConnected()) {
            onAuthorized(actionType);
            return;
        }

        pendingActionRef.current = actionType;
        void connect();
    };

    return { dispatchAction, isConnecting };
};
